import React, { useState } from 'react';
import { updateUserAPI } from '../services/serverService';

const GENDER_OPTIONS = ["Male", "Female", "Non-binary", "Agender", "Prefer not to say", "Other"];
const PERMISSION_OPTIONS = ["Super Admin", "Admin", "Manager", "HR", "Finance", "Default"];
const CONTRACT_TYPES = ["Full-time", "Part-time", "Contractor", "Zero-hour"];

export default function EditAdminForm({ admin, serverId, closeModal, onUpdate }) {
    
    const toInputDate = (dateStr) => {
        if (!dateStr) return "";
        if (dateStr.includes(" ")) {
            const dateObj = new Date(dateStr);
            if (!isNaN(dateObj)) return dateObj.toISOString().split('T')[0];
        }
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
        const parts = dateStr.split(/[-/]/);
        if (parts.length === 3) {
            if (parts[0].length === 4) return `${parts[0]}-${parts[1]}-${parts[2]}`;
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        return "";
    };

    const [formData, setFormData] = useState({
        ...admin,
        phoneCode: admin.phoneCode || '+44',
        dob: toInputDate(admin.dob),
        address: admin.address || {},
        pension: admin.pension || {},
        studentLoan: admin.studentLoan || {},
        permissionLevels: admin.permissionLevels || ["Default"],
        CustomFormLevel: admin.CustomFormLevel || []
    });
    
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('personal');

    const handleBasicChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
    const handleNestedChange = (section, field, value) => setFormData(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));

    const handlePermissionChange = (role) => {
        setFormData(prev => {
            const current = prev.permissionLevels || [];
            return current.includes(role) ? { ...prev, permissionLevels: current.filter(r => r !== role) } : { ...prev, permissionLevels: [...current, role] };
        });
    };

    const handleCustomLevelChange = (role) => {
        setFormData(prev => {
            const current = prev.CustomFormLevel || [];
            return current.includes(role) ? { ...prev, CustomFormLevel: current.filter(r => r !== role) } : { ...prev, CustomFormLevel: [...current, role] };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const payload = {
                fullName: formData.fullName || "",
                username: formData.username || "",
                email: formData.email || "",
                phoneCode: formData.phoneCode || "+44",
                phoneNumber: formData.phoneNumber || "",
                dob: formData.dob || "", 
                gender: formData.gender || "",
                nationality: formData.nationality || "",
                religion: formData.religion || "",
                niNumber: formData.niNumber || "",
                jobRole: formData.jobRole || "",
                contractType: formData.contractType || "",
                employmentStatus: formData.employmentStatus || "",
                workingHours: formData.workingHours || "",
                incomeTaxRate: formData.incomeTaxRate || "",
                address: formData.address || {},
                pension: formData.pension || {},
                studentLoan: formData.studentLoan || {},
                permissionLevels: formData.permissionLevels || [],
                CustomFormLevel: formData.CustomFormLevel || []
            };

            // ID Priority: id (username from doc fetch) > username > uid 
            const targetId = admin.id || admin.username || admin.uid; 
            await updateUserAPI(targetId, {
                ...payload,
                serverId: serverId,
                role: 'admin'
            });

            alert("Admin profile updated successfully!");
            onUpdate();
            closeModal();
        } catch (error) {
            console.error("Error updating admin:", error);
            alert("Failed to update: " + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = { width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', marginBottom: '10px', boxSizing: 'border-box' };
    const labelStyle = { display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px', color: '#555' };
    const tabBtnStyle = (tabName) => ({ padding: '15px 15px', border: 'none', cursor: 'pointer', background: 'transparent', borderBottom: activeTab === tabName ? '3px solid #0056b3' : '3px solid transparent', fontWeight: activeTab === tabName ? 'bold' : '500', color: activeTab === tabName ? '#0056b3' : '#666' });

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'white', width: '90%', maxWidth: '700px', height: '85%', borderRadius: '8px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 5px 15px rgba(0,0,0,0.2)' }}>
                <div style={{ padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>Edit Admin: {formData.username}</h2>
                    <button onClick={closeModal} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#999' }}>×</button>
                </div>

                <div style={{ display: 'flex', background: '#f8f9fa', padding: '0 10px', borderBottom: '1px solid #eee', overflowX: 'auto' }}>
                    {['personal', 'address', 'employment', 'financial', 'permissions'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} style={tabBtnStyle(tab)}>{tab.charAt(0).toUpperCase() + tab.slice(1)}</button>)}
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '25px' }}>
                    <form id="adminEditForm" onSubmit={handleSubmit}>
                        <div style={{ display: activeTab === 'personal' ? 'block' : 'none' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                <div><label style={labelStyle}>Full Name</label><input style={inputStyle} value={formData.fullName || ''} onChange={(e) => handleBasicChange('fullName', e.target.value)} /></div>
                                <div><label style={labelStyle}>Username</label><input style={inputStyle} value={formData.username || ''} onChange={(e) => handleBasicChange('username', e.target.value)} /></div>
                                <div><label style={labelStyle}>Email</label><input style={inputStyle} value={formData.email || ''} onChange={(e) => handleBasicChange('email', e.target.value)} /></div>
                                
                                <div>
                                    <label style={labelStyle}>Phone Number</label>
                                    <div style={{ display: 'flex', gap: '5px' }}>
                                        <input 
                                            type="text"
                                            style={{...inputStyle, width: '60px', marginBottom: 0}} 
                                            value={formData.phoneCode || '+44'} 
                                            onChange={(e) => handleBasicChange('phoneCode', e.target.value)} 
                                            placeholder="+44"
                                        />
                                        <input style={{...inputStyle, flex: 1}} value={formData.phoneNumber || ''} onChange={(e) => handleBasicChange('phoneNumber', e.target.value)} placeholder="7575944496" />
                                    </div>
                                </div>

                                <div><label style={labelStyle}>Date of Birth</label><input type="date" style={inputStyle} value={formData.dob || ''} onChange={(e) => handleBasicChange('dob', e.target.value)} /></div>
                                <div>
                                    <label style={labelStyle}>Gender</label>
                                    <select style={inputStyle} value={formData.gender || ''} onChange={(e) => handleBasicChange('gender', e.target.value)}>
                                        <option value="">Select</option>
                                        {GENDER_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
                                    </select>
                                </div>
                                <div><label style={labelStyle}>Nationality</label><input style={inputStyle} value={formData.nationality || ''} onChange={(e) => handleBasicChange('nationality', e.target.value)} /></div>
                                <div><label style={labelStyle}>Religion</label><input style={inputStyle} value={formData.religion || ''} onChange={(e) => handleBasicChange('religion', e.target.value)} /></div>
                            </div>
                        </div>

                        <div style={{ display: activeTab === 'address' ? 'block' : 'none' }}>
                            <label style={labelStyle}>Street / Address Line 1</label><input style={inputStyle} value={formData.address.street || ''} onChange={(e) => handleNestedChange('address', 'street', e.target.value)} placeholder="e.g. 123 Main St" />
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                <div><label style={labelStyle}>Town</label><input style={inputStyle} value={formData.address.town || ''} onChange={(e) => handleNestedChange('address', 'town', e.target.value)} /></div>
                                <div><label style={labelStyle}>County</label><input style={inputStyle} value={formData.address.county || ''} onChange={(e) => handleNestedChange('address', 'county', e.target.value)} /></div>
                                <div><label style={labelStyle}>Postcode</label><input style={inputStyle} value={formData.address.postalCode || ''} onChange={(e) => handleNestedChange('address', 'postalCode', e.target.value)} /></div>
                                <div><label style={labelStyle}>Country</label><input style={inputStyle} value={formData.address.country || ''} onChange={(e) => handleNestedChange('address', 'country', e.target.value)} /></div>
                            </div>
                        </div>

                        <div style={{ display: activeTab === 'employment' ? 'block' : 'none' }}>
                            <label style={labelStyle}>Job Role</label><input style={inputStyle} value={formData.jobRole || ''} onChange={(e) => handleBasicChange('jobRole', e.target.value)} />
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                <div>
                                    <label style={labelStyle}>Contract Type</label>
                                    <select style={inputStyle} value={formData.contractType || ''} onChange={(e) => handleBasicChange('contractType', e.target.value)}>
                                        <option value="">Select</option>
                                        {CONTRACT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div><label style={labelStyle}>Working Hours</label><input style={inputStyle} value={formData.workingHours || ''} onChange={(e) => handleBasicChange('workingHours', e.target.value)} /></div>
                            </div>
                            <label style={labelStyle}>Employment Status</label><input style={inputStyle} value={formData.employmentStatus || ''} onChange={(e) => handleBasicChange('employmentStatus', e.target.value)} />
                        </div>

                        <div style={{ display: activeTab === 'financial' ? 'block' : 'none' }}>
                            <label style={labelStyle}>NI Number</label><input style={inputStyle} value={formData.niNumber || ''} onChange={(e) => handleBasicChange('niNumber', e.target.value)} />
                            <label style={labelStyle}>Income Tax Rate</label><input style={inputStyle} value={formData.incomeTaxRate || ''} onChange={(e) => handleBasicChange('incomeTaxRate', e.target.value)} />
                            <div style={{ marginTop: '15px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                                <h4 style={{ margin: '0 0 10px 0', color: '#0056b3' }}>Pension Details</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                    <div><label style={labelStyle}>Pension ID</label><input style={inputStyle} value={formData.pension.pensionID || ''} onChange={(e) => handleNestedChange('pension', 'pensionID', e.target.value)} /></div>
                                    <div><label style={labelStyle}>Status</label><input style={inputStyle} value={formData.pension.pensionStatus || ''} onChange={(e) => handleNestedChange('pension', 'pensionStatus', e.target.value)} /></div>
                                </div>
                            </div>
                        </div>

                        <div style={{ display: activeTab === 'permissions' ? 'block' : 'none' }}>
                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ fontSize: '14px', margin: '0 0 10px', color: '#333' }}>Permission Levels (System Access)</h4>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                    {PERMISSION_OPTIONS.map(role => (
                                        <label key={`perm-${role}`} style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', border: '1px solid #eee', borderRadius: '20px', cursor: 'pointer', backgroundColor: formData.permissionLevels?.includes(role) ? '#e6f7ff' : 'white', fontSize: '13px' }}>
                                            <input type="checkbox" checked={formData.permissionLevels?.includes(role)} onChange={() => handlePermissionChange(role)} style={{ marginRight: '8px' }} />{role}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <h4 style={{ fontSize: '14px', margin: '0 0 10px', color: '#333' }}>Custom Form Levels (Departmental)</h4>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                    {["Super Admin", "Admin", "HR", "Finance", "Management"].map(role => (
                                        <label key={`custom-${role}`} style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', border: '1px solid #eee', borderRadius: '20px', cursor: 'pointer', backgroundColor: formData.CustomFormLevel?.includes(role) ? '#f0fff4' : 'white', fontSize: '13px' }}>
                                            <input type="checkbox" checked={formData.CustomFormLevel?.includes(role)} onChange={() => handleCustomLevelChange(role)} style={{ marginRight: '8px' }} />{role}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </form>
                </div>

                <div style={{ padding: '20px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '10px', background: '#f8f9fa' }}>
                    <button onClick={closeModal} style={{ padding: '10px 20px', border: '1px solid #ccc', borderRadius: '4px', background: 'white', cursor: 'pointer' }}>Cancel</button>
                    <button type="submit" form="adminEditForm" disabled={loading} style={{ padding: '10px 20px', border: 'none', borderRadius: '4px', background: '#0056b3', color: 'white', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}>
                        {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
}